import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";

export default function AcceptInvitation() {
  const { t } = useTranslation("auth");
  const { t: tu } = useTranslation("users");
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { login } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setError(t("invitation.invalidHelp")); setLoading(false); return; }
    api.get(`/invitations/info?token=${encodeURIComponent(token)}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || t("invitation.invalidHelp")))
      .finally(() => setLoading(false));
  }, [token, t]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      const { data } = await api.post("/invitations/accept", { token, name, password });
      if (data?.token) localStorage.setItem("labora_token", data.token);
      const res = await login(info.email, password);
      if (res.ok) {
        navigate(`/${data.organization.slug}/dashboard`, { replace: true });
      } else {
        setError(res.error || t("invitation.acceptError"));
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || t("invitation.acceptError"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <CenteredCard><div className="text-sm text-zinc-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {t("invitation.loading")}</div></CenteredCard>;
  }

  if (error && !info) {
    return (
      <CenteredCard>
        <div data-testid="invite-error" className="text-center space-y-3">
          <AlertCircle className="w-10 h-10 text-red-600 mx-auto" />
          <div className="font-display font-semibold text-zinc-950 text-lg">{t("invitation.invalidTitle")}</div>
          <p className="text-sm text-zinc-500">{error}</p>
          <a href="/login" className="inline-block text-sm text-zinc-700 underline hover:text-zinc-900">{t("forgot.back")}</a>
        </div>
      </CenteredCard>
    );
  }

  const org = info?.organization || {};
  const roleLabel = tu(`roles.${info?.role}`, info?.role);

  return (
    <CenteredCard>
      <div data-testid="accept-invitation-page" className="space-y-5">
        <div className="flex items-center gap-3 pb-4 border-b border-zinc-100">
          {org.logo_url ? (
            <img src={org.logo_url} alt="" className="w-12 h-12 rounded-md object-cover bg-zinc-100" />
          ) : (
            <div className="w-12 h-12 rounded-md flex items-center justify-center text-white text-base font-semibold" style={{ background: org.primary_color || "#0a0a0a" }}>
              {(org.name || "?").slice(0, 1)}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium">{t("invitation.role")}</div>
            <div data-testid="invite-org-name" className="font-display font-bold text-lg text-zinc-950 truncate">{org.name}</div>
          </div>
        </div>

        <div className="text-sm text-zinc-700">
          {t("invitation.title", { org: org.name })} <span data-testid="invite-role-label" className="inline-flex items-center px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-800 text-xs font-semibold">{roleLabel}</span>
        </div>

        <form onSubmit={submit} className="space-y-3">
          {error && (
            <div data-testid="accept-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
          )}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">{t("invitation.email")}</label>
            <input value={info?.email || ""} disabled className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 bg-zinc-50 text-sm text-zinc-600" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">{t("invitation.name")}</label>
            <input data-testid="accept-name" required minLength={2} value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 text-sm" placeholder={t("invitation.namePlaceholder")} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">{t("invitation.password")} <span className="text-zinc-400 font-normal normal-case">— {t("invitation.passwordHelp")}</span></label>
            <input data-testid="accept-password" required minLength={6} type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 text-sm" placeholder="••••••" />
          </div>
          <button data-testid="accept-submit" type="submit" disabled={submitting} className="w-full bg-zinc-950 hover:bg-zinc-800 text-white font-semibold py-2.5 rounded-lg disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {submitting ? t("invitation.saving") : t("invitation.submit")}
          </button>
        </form>
      </div>
    </CenteredCard>
  );
}

function CenteredCard({ children }) {
  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4 py-12 relative">
      <div className="absolute top-4 right-4"><LanguageSwitcher /></div>
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-2xl shadow-sm p-8">
        {children}
      </div>
    </div>
  );
}
